import { useEffect, useState, useMemo, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ExternalLink,
  Layers,
  Globe,
  User,
  Sparkles,
  FileText,
} from "lucide-react";
import { ROLE_COLORS, ROLE_LABELS } from "@/components/map-visual/constants";
import type {
  ThematicMap,
  ThematicSeed,
  ThematicNode,
  ThematicNodeContent,
  ThematicWritingProfile,
} from "@/types/thematicMaps";

interface MapPresentationViewProps {
  map: ThematicMap;
  seeds: ThematicSeed[];
  nodes: ThematicNode[];
  contents: ThematicNodeContent[];
  activeProfile: ThematicWritingProfile | null;
}

type CardStatus = "published" | "ready" | "planned";

const STATUS_PALETTE: Record<
  CardStatus,
  { border: string; badgeBg: string; badgeText: string; label: string }
> = {
  published: {
    border: "border-l-emerald-500",
    badgeBg: "bg-emerald-500/20",
    badgeText: "text-emerald-300",
    label: "opublikowany",
  },
  ready: {
    border: "border-l-sky-500",
    badgeBg: "bg-sky-500/20",
    badgeText: "text-sky-300",
    label: "gotowa treść",
  },
  planned: {
    border: "border-l-zinc-700",
    badgeBg: "bg-zinc-700/40",
    badgeText: "text-zinc-300",
    label: "planowany",
  },
};

// Mapowanie surowych enumów DemandTier na polskie etykiety.
// no_demand / unknown / null / brak klucza → chip NIE renderowany (showChips=false).
const DEMAND_LABELS: Record<string, string> = {
  weak_signal: "popyt: niski",
  validated_moderate: "popyt: umiarkowany",
  clearly_validated: "popyt: wysoki",
};

const SEED_PALETTE = [
  "#7c8cf8",
  "#5bd6a6",
  "#f2a65a",
  "#e0729b",
  "#62b6e8",
  "#b98ae0",
  "#4fcfbe",
  "#ee8c77",
  "#9fd356",
  "#facc15",
];

function getCardStatus(content: ThematicNodeContent | undefined): CardStatus {
  if (!content) return "planned";
  if (content.engine_stage === "published" || content.published_at)
    return "published";
  if (
    content.engine_stage === "ready" ||
    content.content_status === "content_ready" ||
    content.content_status === "approved"
  )
    return "ready";
  return "planned";
}

function wordCountOf(content: ThematicNodeContent | undefined): number {
  if (!content) return 0;
  const html = content.final_html || "";
  if (html) {
    return html
      .replace(/<[^>]+>/g, " ")
      .split(/\s+/)
      .filter(Boolean).length;
  }
  return content.word_count_target || 0;
}

function formatNumber(n: number): string {
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return String(n);
}

function detectPlatform(url: string): string {
  const lc = (url || "").toLowerCase();
  if (lc.includes("linkedin")) return "LinkedIn";
  if (lc.includes("twitter") || lc.includes("x.com")) return "X / Twitter";
  if (lc.includes("github")) return "GitHub";
  if (lc.includes("facebook")) return "Facebook";
  if (lc.includes("instagram")) return "Instagram";
  if (lc.includes("youtube")) return "YouTube";
  try {
    const u = new URL(url);
    return u.hostname.replace("www.", "");
  } catch {
    return "Link";
  }
}

export function MapPresentationView({
  map,
  seeds,
  nodes,
  contents,
  activeProfile,
}: MapPresentationViewProps) {
  const [website, setWebsite] = useState<{
    url: string | null;
    name: string | null;
  } | null>(null);

  useEffect(() => {
    if (!map?.website_id) {
      setWebsite(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data } = await (supabase as any)
          .from("websites")
          .select("url, name")
          .eq("id", map.website_id)
          .maybeSingle();
        if (!cancelled && data) {
          setWebsite({ url: data.url ?? null, name: data.name ?? null });
        }
      } catch {
        /* swallow — brak website = no link, view dalej renderuje */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [map?.website_id]);

  const safeSeeds = useMemo(() => seeds || [], [seeds]);
  const safeNodes = useMemo(() => nodes || [], [nodes]);
  const safeContents = useMemo(() => contents || [], [contents]);

  const contentByNodeId = useMemo(() => {
    const m = new Map<string, ThematicNodeContent>();
    for (const c of safeContents) {
      if (c?.node_id) m.set(c.node_id, c);
    }
    return m;
  }, [safeContents]);

  const seedStats = useMemo(() => {
    return safeSeeds.map((seed, idx) => {
      const seedNodes = safeNodes.filter((n) => n.seed_id === seed.id);
      const counts = {
        total: seedNodes.length,
        published: 0,
        ready: 0,
        planned: 0,
      };
      for (const n of seedNodes) {
        const s = getCardStatus(contentByNodeId.get(n.id));
        counts[s]++;
      }
      return {
        seed,
        nodes: seedNodes,
        counts,
        color: SEED_PALETTE[idx % SEED_PALETTE.length],
      };
    });
  }, [safeSeeds, safeNodes, contentByNodeId]);

  const kpi = useMemo(() => {
    let published = 0;
    let ready = 0;
    let totalWords = 0;
    let totalQuality = 0;
    let qualityCount = 0;
    for (const n of safeNodes) {
      const c = contentByNodeId.get(n.id);
      const s = getCardStatus(c);
      if (s === "published") published++;
      if (s === "ready") ready++;
      if (c) {
        totalWords += wordCountOf(c);
        if (c.quality_score != null) {
          totalQuality += c.quality_score;
          qualityCount++;
        }
      }
    }
    const withContent = published + ready;
    const coverage = safeNodes.length
      ? Math.round((published / safeNodes.length) * 100)
      : 0;
    const avgQuality = qualityCount
      ? Math.round(totalQuality / qualityCount)
      : 0;
    return {
      seedsCount: safeSeeds.length,
      nodesCount: safeNodes.length,
      withContent,
      published,
      coverage,
      totalWords,
      avgQuality,
    };
  }, [safeNodes, contentByNodeId, safeSeeds.length]);

  if (!map) {
    return (
      <div className="rounded-xl border border-white/10 bg-zinc-900/40 p-10 text-center text-sm text-zinc-500">
        Brak danych mapy.
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-[#0a0a10] text-zinc-100 px-5 py-6 space-y-10 border border-white/10 max-w-[1400px] mx-auto">
      <HeroSection map={map} website={website} kpi={kpi} />
      <AuthorProfileSection profile={activeProfile} />
      <ClusterNavBar seedStats={seedStats} />
      {seedStats.map(({ seed, nodes: seedNodes, counts, color }) => (
        <SeedSection
          key={seed.id}
          seed={seed}
          color={color}
          nodes={seedNodes}
          counts={counts}
          contentByNodeId={contentByNodeId}
        />
      ))}
      <FooterSection map={map} kpi={kpi} seedsCount={seedStats.length} />
    </div>
  );
}

// =============================================
// HERO
// =============================================

function HeroSection({
  map,
  website,
  kpi,
}: {
  map: ThematicMap;
  website: { url: string | null; name: string | null } | null;
  kpi: {
    seedsCount: number;
    nodesCount: number;
    withContent: number;
    published: number;
    coverage: number;
    totalWords: number;
    avgQuality: number;
  };
}) {
  const macro = map.macro_statement?.trim() || "";
  const audience = map.audience?.trim() || "";
  const scopeIn = (map.scope_in || []).filter((s) => s && s.trim());
  const scopeOut = (map.scope_out || []).filter((s) => s && s.trim());
  const hasMeta = !!(audience || scopeIn.length || scopeOut.length);

  return (
    <section id="top">
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-950/60 via-zinc-900 to-black p-7 lg:p-10">
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-fuchsia-500/15 blur-3xl pointer-events-none" />
        <div className="relative">
          <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-fuchsia-400">
            Mapa tematyczna · podgląd dla klienta
          </p>
          <h1 className="mt-2 text-3xl lg:text-5xl font-bold tracking-tight">
            {map.name || "Mapa tematyczna"}
          </h1>
          {website?.url ? (
            <a
              href={website.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-sm text-sky-400 hover:text-sky-300 transition-colors"
            >
              <Globe className="h-3.5 w-3.5" />
              {website.url}
              <ExternalLink className="h-3 w-3 opacity-70" />
            </a>
          ) : website?.name ? (
            <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-zinc-400">
              <Globe className="h-3.5 w-3.5" />
              {website.name}
            </p>
          ) : null}
          {macro && (
            <p className="mt-5 max-w-3xl text-[15px] leading-relaxed text-zinc-300">
              {macro}
            </p>
          )}
          {hasMeta && (
            <div className="mt-5 space-y-2.5">
              {audience && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                    Audience:
                  </span>
                  <span className="text-sm text-zinc-200">{audience}</span>
                </div>
              )}
              {scopeIn.length > 0 && (
                <div className="flex items-start gap-2 flex-wrap">
                  <span className="text-[11px] uppercase tracking-wider text-emerald-400/80 font-semibold pt-0.5">
                    Scope IN:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {scopeIn.map((s, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {scopeOut.length > 0 && (
                <div className="flex items-start gap-2 flex-wrap">
                  <span className="text-[11px] uppercase tracking-wider text-rose-400/80 font-semibold pt-0.5">
                    Scope OUT:
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {scopeOut.map((s, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-0.5 rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-300"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KpiCard label="Klastry" value={kpi.seedsCount} variant="primary" />
        <KpiCard label="Artykuły" value={kpi.nodesCount} />
        <KpiCard label="Z treścią" value={kpi.withContent} variant="blue" />
        <KpiCard
          label="Opublikowane"
          value={kpi.published}
          variant="green"
        />
        <KpiCard
          label="Pokrycie"
          value={kpi.nodesCount ? `${kpi.coverage}%` : "—"}
        />
        <KpiCard
          label="Słowa"
          value={kpi.totalWords ? formatNumber(kpi.totalWords) : "—"}
        />
        <KpiCard
          label="Śr. jakość"
          value={kpi.avgQuality ? `${kpi.avgQuality}` : "—"}
        />
      </div>
    </section>
  );
}

function KpiCard({
  label,
  value,
  variant,
}: {
  label: string;
  value: string | number;
  variant?: "primary" | "blue" | "green";
}) {
  const valColor =
    variant === "primary"
      ? "text-fuchsia-400"
      : variant === "blue"
        ? "text-sky-400"
        : variant === "green"
          ? "text-emerald-400"
          : "text-zinc-100";
  return (
    <div className="rounded-xl border border-white/10 bg-zinc-900/50 px-4 py-3.5">
      <div className={`text-2xl font-bold leading-tight ${valColor}`}>
        {value}
      </div>
      <div className="mt-1 text-[10.5px] uppercase tracking-wider text-zinc-500 font-medium">
        {label}
      </div>
    </div>
  );
}

// =============================================
// AUTHOR PROFILE
// =============================================

function AuthorProfileSection({
  profile,
}: {
  profile: ThematicWritingProfile | null;
}) {
  const [showAuthorPage, setShowAuthorPage] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [showFullProfile, setShowFullProfile] = useState(false);
  const [bioOverflows, setBioOverflows] = useState(false);
  const bioRef = useRef<HTMLDivElement>(null);

  if (!profile) return null;

  const dnaChips: Array<{ label: string; value: string }> = [];
  if (profile.voice_pronouns)
    dnaChips.push({ label: "ton", value: String(profile.voice_pronouns) });
  if (profile.verbosity)
    dnaChips.push({
      label: "verbosity",
      value: String(profile.verbosity),
    });
  if (profile.sentence_length)
    dnaChips.push({
      label: "zdania",
      value: String(profile.sentence_length),
    });
  if (profile.analogy_frequency)
    dnaChips.push({
      label: "analogie",
      value: String(profile.analogy_frequency),
    });
  if (profile.claim_strength)
    dnaChips.push({
      label: "claim",
      value: String(profile.claim_strength),
    });
  if (profile.difficulty_level)
    dnaChips.push({
      label: "poziom",
      value: String(profile.difficulty_level),
    });

  const voiceChars = (profile.voice_characteristics || []).filter(
    (v) => v && v.trim(),
  );
  const knowsAbout = (profile.author_knows_about || []).filter(
    (v) => v && v.trim(),
  );
  const sameAs = (profile.author_same_as || []).filter((v) => v && v.trim());
  const bio = profile.author_bio_html?.trim() || "";
  const identity = profile.author_identity?.trim() || "";
  const mission = profile.mission?.trim() || "";

  const displayName =
    profile.public_author_label?.trim() ||
    profile.profile_name?.trim() ||
    profile.name?.trim() ||
    "Autor";

  const initials =
    displayName
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";

  const hasAuthorPage = !!profile.author_page_html?.trim();
  const photoUrl = profile.author_photo_url?.trim() || "";
  const showPhoto = !!photoUrl && !photoError;

  const hasExpandedContent =
    !!mission ||
    voiceChars.length > 0 ||
    knowsAbout.length > 0 ||
    sameAs.length > 0 ||
    hasAuthorPage;

  // Mierz czy bio realnie przekracza maxHeight (4.5em) — button "Rozwiń" tylko gdy overflow.
  // Mierzymy w stanie collapsed (bioExpanded=false), bo wtedy maxHeight ogranicza clientHeight.
  // W stanie expanded maxHeight=undefined → scrollHeight==clientHeight (measurement błędny, skip).
  useEffect(() => {
    if (!bio || bioExpanded) return;
    const el = bioRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      // +1px bufor dla sub-pixel rounding
      setBioOverflows(el.scrollHeight > el.clientHeight + 1);
    });
    return () => cancelAnimationFrame(id);
  }, [bio, bioExpanded]);

  return (
    <section id="profile">
      <div className="relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-pink-950/30 via-zinc-900 to-zinc-950 p-4 lg:p-5">
        <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-pink-500/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-start gap-4">
          {/* Avatar (compact 56px) */}
          <div className="flex-shrink-0">
            <div className="relative">
              {showPhoto ? (
                <img
                  src={photoUrl}
                  alt={displayName}
                  className="w-14 h-14 rounded-lg object-cover border border-white/10"
                  onError={() => setPhotoError(true)}
                  loading="lazy"
                />
              ) : (
                <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-pink-500 to-fuchsia-700 flex items-center justify-center text-lg font-bold text-white border border-white/10">
                  {initials}
                </div>
              )}
              <div className="absolute -top-1 -right-1 bg-pink-500 rounded-full p-0.5">
                <User className="h-2.5 w-2.5 text-white" />
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0 space-y-2">
            {/* Title row: eyebrow tiny + name + jobtitle inline + author_url */}
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] text-pink-400/80 font-bold">
                Profil autora
              </p>
              <div className="flex items-baseline gap-2 flex-wrap mt-0.5">
                <h2 className="text-lg lg:text-xl font-bold leading-tight">
                  {displayName}
                </h2>
                {profile.author_job_title && (
                  <span className="text-xs text-pink-300/80">
                    · {profile.author_job_title}
                  </span>
                )}
                {profile.author_url && (
                  <a
                    href={profile.author_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-0.5 text-[11px] text-pink-400 hover:text-pink-300"
                  >
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                )}
              </div>
            </div>

            {/* Bio z toggle Rozwiń (line-clamp na HTML nie działa — używamy maxHeight) */}
            {bio && (
              <div>
                <div
                  ref={bioRef}
                  className="prose prose-sm prose-invert max-w-none prose-p:text-zinc-300 prose-p:leading-snug prose-p:my-1 prose-headings:text-zinc-100 prose-strong:text-zinc-100 prose-a:text-pink-300"
                  style={
                    bioExpanded
                      ? undefined
                      : { maxHeight: "4.5em", overflow: "hidden" }
                  }
                  dangerouslySetInnerHTML={{ __html: bio }}
                />
                {(bioOverflows || bioExpanded) && (
                  <button
                    type="button"
                    onClick={() => setBioExpanded(!bioExpanded)}
                    className="text-[11px] text-pink-400 hover:text-pink-300 mt-1"
                  >
                    {bioExpanded ? "Zwiń bio" : "Rozwiń bio"}
                  </button>
                )}
              </div>
            )}
            {!bio && identity && (
              <p className="text-sm text-zinc-300 leading-snug line-clamp-3">
                {identity}
              </p>
            )}

            {/* DNA chips — 1 rząd, bez header */}
            {dnaChips.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {dnaChips.map(({ label, value }) => (
                  <span
                    key={label}
                    className="text-[10.5px] px-2 py-0.5 rounded-md bg-zinc-800/80 border border-white/10"
                  >
                    <span className="text-zinc-500">{label}: </span>
                    <span className="text-zinc-200 font-medium">{value}</span>
                  </span>
                ))}
              </div>
            )}

            {/* Toggle "Pokaż pełen profil" — renderuj tylko gdy są expanded sekcje */}
            {hasExpandedContent && (
              <button
                type="button"
                onClick={() => setShowFullProfile(!showFullProfile)}
                className="text-[11px] text-pink-400 hover:text-pink-300 inline-flex items-center gap-1"
              >
                {showFullProfile
                  ? "Zwiń pełen profil"
                  : "Pokaż pełen profil"}
              </button>
            )}

            {/* Expanded sections — mission, voiceChars, knowsAbout, sameAs, button strony autora */}
            {showFullProfile && hasExpandedContent && (
              <div className="space-y-3 pt-2 border-t border-white/5">
                {mission && (
                  <div className="flex items-start gap-2 p-2.5 rounded-lg border border-pink-500/20 bg-pink-500/5">
                    <Sparkles className="h-3.5 w-3.5 text-pink-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-zinc-300 italic leading-snug">
                      {mission}
                    </p>
                  </div>
                )}
                {voiceChars.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">
                      Charakter
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {voiceChars.map((v, i) => (
                        <span
                          key={i}
                          className="text-[10.5px] px-2 py-0.5 rounded-md bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-200"
                        >
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {knowsAbout.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">
                      Eksperckie obszary
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {knowsAbout.map((v, i) => (
                        <span
                          key={i}
                          className="text-[10.5px] px-2 py-0.5 rounded-md bg-sky-500/10 border border-sky-500/30 text-sky-300"
                        >
                          {v}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {sameAs.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 items-center">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
                      Social:
                    </p>
                    {sameAs.map((url, i) => (
                      <a
                        key={i}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[10.5px] px-2 py-0.5 rounded-md border border-white/10 bg-zinc-800/50 hover:bg-zinc-800 text-zinc-200 transition-colors inline-flex items-center gap-1"
                      >
                        {detectPlatform(url)}{" "}
                        <ExternalLink className="h-2.5 w-2.5 opacity-60" />
                      </a>
                    ))}
                  </div>
                )}
                {hasAuthorPage && (
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowAuthorPage(true)}
                      className="border-pink-500/30 hover:border-pink-500/50 text-pink-300 hover:text-pink-200 bg-pink-500/5 hover:bg-pink-500/10 h-7 text-xs"
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      Podejrzyj stronę autora
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {hasAuthorPage && showAuthorPage && profile.author_page_html && (
        <Dialog open onOpenChange={(o) => !o && setShowAuthorPage(false)}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle>Strona autora: {displayName}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto">
              <div
                className="prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{
                  __html: profile.author_page_html,
                }}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}

// =============================================
// CLUSTER NAV BAR (sticky)
// =============================================

function ClusterNavBar({
  seedStats,
}: {
  seedStats: Array<{
    seed: ThematicSeed;
    nodes: ThematicNode[];
    counts: { total: number; published: number; ready: number; planned: number };
    color: string;
  }>;
}) {
  if (!seedStats.length) return null;

  return (
    <nav className="sticky top-0 z-30 -mx-5 px-5 py-3 bg-zinc-950/85 backdrop-blur-md border-y border-white/5">
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <a
          href="#top"
          className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-zinc-900 border border-fuchsia-500/40 text-zinc-100 hover:border-fuchsia-500 transition-colors whitespace-nowrap"
        >
          <span className="w-2 h-2 rounded-full bg-fuchsia-500" />
          Mapa
        </a>
        <a
          href="#profile"
          className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-zinc-900/80 border border-white/10 text-zinc-300 hover:border-pink-500/40 hover:text-zinc-100 transition-colors whitespace-nowrap"
        >
          <span className="w-2 h-2 rounded-full bg-pink-500" />
          Autor
        </a>
        {seedStats.map(({ seed, counts, color }) => (
          <a
            key={seed.id}
            href={`#seed-${seed.id}`}
            className="flex-shrink-0 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-zinc-900/80 border border-white/10 text-zinc-300 hover:border-white/30 hover:text-zinc-100 transition-colors whitespace-nowrap"
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: color }}
            />
            <span className="max-w-[220px] truncate">
              {seed.name || "Klaster"}
            </span>
            <span className="text-zinc-500 font-medium">{counts.total}</span>
          </a>
        ))}
      </div>
    </nav>
  );
}

// =============================================
// SEED SECTION
// =============================================

function SeedSection({
  seed,
  color,
  nodes,
  counts,
  contentByNodeId,
}: {
  seed: ThematicSeed;
  color: string;
  nodes: ThematicNode[];
  counts: { total: number; published: number; ready: number; planned: number };
  contentByNodeId: Map<string, ThematicNodeContent>;
}) {
  const sortedNodes = useMemo(() => {
    const rank: Record<CardStatus, number> = {
      published: 0,
      ready: 1,
      planned: 2,
    };
    return [...nodes].sort((a, b) => {
      const ca = contentByNodeId.get(a.id);
      const cb = contentByNodeId.get(b.id);
      const sa = getCardStatus(ca);
      const sb = getCardStatus(cb);
      if (rank[sa] !== rank[sb]) return rank[sa] - rank[sb];
      const qa = ca?.quality_score ?? 0;
      const qb = cb?.quality_score ?? 0;
      if (qa !== qb) return qb - qa;
      return wordCountOf(cb) - wordCountOf(ca);
    });
  }, [nodes, contentByNodeId]);

  const total = counts.total || 0;
  const pubPct = total ? (counts.published / total) * 100 : 0;
  const readyPct = total ? (counts.ready / total) * 100 : 0;
  const planPct = total ? 100 - pubPct - readyPct : 0;

  const hubTitle = seed.hub_title?.trim() || "";
  const showHub = hubTitle && hubTitle !== seed.name;

  return (
    <section id={`seed-${seed.id}`} className="scroll-mt-24">
      <div className="flex items-center gap-4 pb-4 border-b border-white/10">
        <span
          className="w-4 h-4 rounded flex-shrink-0"
          style={{ backgroundColor: color }}
        />
        <div className="flex-1 min-w-0">
          <h2 className="text-xl lg:text-2xl font-bold text-zinc-100 truncate">
            {seed.name || "Klaster"}
          </h2>
          {showHub && (
            <p className="text-xs text-zinc-500 mt-0.5 truncate italic">
              hub: {hubTitle}
            </p>
          )}
          <p className="text-[12.5px] text-zinc-500 mt-1">
            {total} {total === 1 ? "artykuł" : "artykułów"}
            {counts.published > 0 && ` · ${counts.published} opublikowanych`}
            {counts.ready > 0 && ` · ${counts.ready} z gotową treścią`}
          </p>
          {total > 0 && (
            <div className="mt-2 flex h-1.5 rounded-full overflow-hidden max-w-[340px] bg-zinc-800">
              {pubPct > 0 && (
                <div
                  className="bg-emerald-500"
                  style={{ width: `${pubPct}%` }}
                />
              )}
              {readyPct > 0 && (
                <div className="bg-sky-500" style={{ width: `${readyPct}%` }} />
              )}
              {planPct > 0 && (
                <div className="bg-zinc-700" style={{ width: `${planPct}%` }} />
              )}
            </div>
          )}
        </div>
        <div
          className="text-4xl lg:text-5xl font-bold opacity-50 leading-none"
          style={{ color }}
        >
          {total}
        </div>
      </div>

      {sortedNodes.length === 0 ? (
        <div className="mt-6 border-2 border-dashed border-white/10 rounded-xl p-10 text-center">
          <FileText className="h-8 w-8 mx-auto mb-2 text-zinc-700" />
          <p className="text-sm text-zinc-500">Brak artykułów w tym klastrze</p>
        </div>
      ) : (
        <div
          className="mt-5 grid gap-3.5"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(322px, 1fr))",
          }}
        >
          {sortedNodes.map((node) => (
            <ArticleCard
              key={node.id}
              node={node}
              content={contentByNodeId.get(node.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// =============================================
// ARTICLE CARD
// =============================================

function ArticleCard({
  node,
  content,
}: {
  node: ThematicNode;
  content: ThematicNodeContent | undefined;
}) {
  const status = getCardStatus(content);
  const palette = STATUS_PALETTE[status];

  const roleKey = node.document_role || "";
  const roleLabel = ROLE_LABELS[roleKey] || (roleKey ? roleKey : "");
  const roleColor = ROLE_COLORS[roleKey];

  const opening =
    content?.opening_answer?.trim() || content?.meta_description?.trim() || "";
  const wc = wordCountOf(content);
  const qa = content?.quality_score ?? null;
  const h2Count = content?.h2_outline?.length ?? 0;
  const demand =
    node.demand_tier && DEMAND_LABELS[node.demand_tier]
      ? DEMAND_LABELS[node.demand_tier]
      : "";
  const nodeRole = (node as { node_role?: string }).node_role;
  const isPillar = roleKey === "pillar" || nodeRole === "pillar";
  const liveUrl = content?.wp_post_url || "";
  const slug = node.slug?.trim() || "";

  const showMetaLine = wc > 0 || qa != null || h2Count > 0;
  const showChips = !!demand;

  return (
    <div
      className={`relative overflow-hidden rounded-xl border border-white/10 bg-zinc-900/60 p-4 transition-all hover:border-white/20 hover:bg-zinc-900/80 border-l-4 ${palette.border}`}
    >
      <div className="flex items-center gap-2 mb-2.5">
        {isPillar ? (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-violet-500/20 text-violet-300 border border-violet-500/40 inline-flex items-center gap-1">
            <Layers className="h-2.5 w-2.5" />
            PILLAR
          </span>
        ) : roleLabel ? (
          <span
            className="text-[10px] font-medium px-2 py-0.5 rounded border"
            style={{
              borderColor: `${roleColor || "#666"}40`,
              backgroundColor: `${roleColor || "#666"}15`,
              color: roleColor || "#aaa",
            }}
          >
            {roleLabel}
          </span>
        ) : null}
        <span
          className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded ${palette.badgeBg} ${palette.badgeText} uppercase tracking-wider`}
        >
          {palette.label}
        </span>
      </div>

      <h3 className="text-[14.5px] font-semibold leading-snug text-zinc-100 mb-2 line-clamp-2">
        {node.title || "(bez tytułu)"}
      </h3>

      {showMetaLine && (
        <div className="text-[11.5px] font-medium mb-2 flex items-center gap-2 flex-wrap">
          {wc > 0 && <span className="text-fuchsia-400/90">{wc} słów</span>}
          {qa != null && (
            <>
              {wc > 0 && <span className="text-zinc-700">·</span>}
              <span
                className={
                  qa >= 70
                    ? "text-emerald-400"
                    : qa >= 40
                      ? "text-amber-400"
                      : "text-rose-400"
                }
              >
                QA {qa}
              </span>
            </>
          )}
          {h2Count > 0 && (
            <>
              {(wc > 0 || qa != null) && (
                <span className="text-zinc-700">·</span>
              )}
              <span className="text-sky-400">{h2Count} H2</span>
            </>
          )}
        </div>
      )}

      {status !== "planned" && opening ? (
        <p className="text-[12.5px] text-zinc-400 leading-relaxed mb-2.5 line-clamp-4">
          {opening}
        </p>
      ) : status === "planned" ? (
        <p className="text-[12px] text-zinc-600 italic mb-2.5">
          Zaplanowany — gotowy do produkcji
        </p>
      ) : null}

      {showChips && (
        <div className="flex flex-wrap gap-1 mb-2.5">
          {demand && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300">
              {demand}
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-2 border-t border-white/5">
        {slug ? (
          <code
            className="text-[10px] text-zinc-600 font-mono truncate flex-1"
            title={`/${slug}`}
          >
            /{slug}
          </code>
        ) : (
          <span className="flex-1" />
        )}
        {liveUrl && (
          <a
            href={liveUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 flex-shrink-0"
            title="Zobacz live"
          >
            live <ExternalLink className="h-2.5 w-2.5" />
          </a>
        )}
      </div>
    </div>
  );
}

// =============================================
// FOOTER
// =============================================

function FooterSection({
  map,
  kpi,
  seedsCount,
}: {
  map: ThematicMap;
  kpi: {
    nodesCount: number;
    published: number;
    totalWords: number;
  };
  seedsCount: number;
}) {
  const klasterWord =
    seedsCount === 1 ? "klaster" : seedsCount < 5 && seedsCount > 1 ? "klastry" : "klastrów";
  return (
    <footer className="text-center text-xs text-zinc-600 pt-8 pb-4">
      Mapa tematyczna {map.name || ""} · {seedsCount} {klasterWord} ·{" "}
      {kpi.nodesCount} {kpi.nodesCount === 1 ? "artykuł" : "artykułów"}
      {kpi.published > 0 && ` · ${kpi.published} opublikowanych`}
      {kpi.totalWords > 0 && ` · ~${formatNumber(kpi.totalWords)} słów`}
    </footer>
  );
}
